import { TimeSince } from '@app/Components/DateTime/TimeSince';
import { formatDistanceToNow } from 'date-fns';
import * as React from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import type { ToastPosition } from 'react-bootstrap/esm/ToastContainer';
import type { Variant } from 'react-bootstrap/esm/types';
import { useNavigate } from 'react-router-dom';

const ToastContext = React.createContext(null);

export enum ToastDuration {
  Short = 3000,
  Long = 10000,
}

type ToastProviderProps = {
  children: React.ReactElement;
  position: ToastPosition;
};

type ToastProps = {
  id: number;
  title?: string;
  autohide?: boolean;
  message: string;
  dateTime: Date;
  variant?: Variant;
  href?: string;
  delay: ToastDuration;
};

const DefaultToast = ({
  id,
  title,
  message,
  autohide = false,
  dateTime,
  variant,
  href,
  delay = ToastDuration.Short,
}: ToastProps) => {
  const [show, setShow] = React.useState<boolean>(true);
  const navigate = useNavigate();

  const toastClassList = [];
  const toastBodyClassList = ['text-dark'];

  if (href) {
    toastBodyClassList.push('cursor-pointer', 'hover-shadow-inset');
  }

  if (variant) {
    toastClassList.push('border-' + variant);
  }

  return (
    <Toast
      show={show}
      bg="white"
      autohide={autohide}
      delay={delay}
      className={toastClassList.join(' ')}
      onClose={() => setShow(false)}
    >
      {title && (
        <Toast.Header>
          <strong className="me-auto">{title}</strong>
          <small>
            <TimeSince date={dateTime} />
          </small>
        </Toast.Header>
      )}
      <Toast.Body
        className={toastBodyClassList.join(' ')}
        onClick={href ? () => navigate(href) : undefined}
      >
        {message}
      </Toast.Body>
    </Toast>
  );
};

export const ToastProvider = ({ children, position = 'bottom-start' }: ToastProviderProps) => {
  const [toasts, setToasts] = React.useState<ToastProps[]>([]);

  const notify = (toast: Omit<ToastProps, 'id'>) => {
    setToasts((current) => [
      ...current,
      {
        ...toast,
        id: Date.now(),
        dateTime: new Date(),
      },
    ]);
  };

  return (
    <ToastContext.Provider value={{ notify }}>
      {children}

      <ToastContainer position={position} className="p-3">
        {toasts.map((toast) => (
          <DefaultToast key={toast.id} {...toast} />
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};

export const useToast = () => React.useContext(ToastContext);
